import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ComputerDesktopIcon,
  SignalIcon,
  ExclamationTriangleIcon,
  WrenchScrewdriverIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  PlusIcon,
  BugAntIcon,
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  ChevronDownIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import * as api from '../services/api';
import { BillingPill, SilentHostsBanner } from './billing/BillingPill';
import { deriveBillingState, type BillingState } from './billing/billingState';

// Types
interface Agent {
  id: string;
  name: string;
  hostname: string;
  ipAddress?: string;
  status: 'ONLINE' | 'OFFLINE' | 'ERROR' | 'MAINTENANCE';
  agentVersion: string;
  osVersion?: string;
  lastSeenAt: string;
  registeredAt: string;
  archivedAt?: string;
  client: {
    id: string;
    name: string;
  };
  debugErrorCount?: number;
  isArchived?: boolean;
  /** Billing view from the server (Server #146); derived from lastSeenAt when absent. */
  silentDays?: number | null;
  billingState?: BillingState;
}

interface FleetStats {
  total: number;
  online: number;
  offline: number;
  error: number;
  maintenance: number;
  lastUpdated: string;
}

interface AgentFilter {
  clientId?: string;
  status?: string;
  search?: string;
  billing?: BillingState;
}

interface AgentFleetDashboardProps {
  tenantId: string;
  onAgentSelect?: (agent: Agent) => void;
  onAgentRegister?: () => void;
}

const statusColors = {
  ONLINE: 'text-green-600 bg-green-100',
  OFFLINE: 'text-gray-600 bg-gray-100',
  ERROR: 'text-red-600 bg-red-100',
  MAINTENANCE: 'text-yellow-600 bg-yellow-100'
};

const statusIcons = {
  ONLINE: SignalIcon,
  OFFLINE: ComputerDesktopIcon,
  ERROR: ExclamationTriangleIcon,
  MAINTENANCE: WrenchScrewdriverIcon
};

export const AgentFleetDashboard: React.FC<AgentFleetDashboardProps> = ({
  tenantId,
  onAgentSelect,
  onAgentRegister
}) => {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [archivedAgents, setArchivedAgents] = useState<Agent[]>([]);
  const [fleetStats, setFleetStats] = useState<FleetStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState<AgentFilter>({});
  const [selectedAgents, setSelectedAgents] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [archiveConfirm, setArchiveConfirm] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages] = useState(1);

  // Derive unique clients from loaded agents for the filter dropdown
  const uniqueClients = React.useMemo(() => {
    const clientMap = new Map<string, string>();
    agents.forEach(agent => {
      if (agent.client?.id && agent.client?.name) {
        clientMap.set(agent.client.id, agent.client.name);
      }
    });
    return Array.from(clientMap.entries()).sort((a, b) => a[1].localeCompare(b[1]));
  }, [agents]);

  // Apply filters client-side
  const filteredAgents = React.useMemo(() => {
    return agents.filter(agent => {
      if (filter.status && agent.status !== filter.status) return false;
      if (filter.clientId && agent.client?.id !== filter.clientId) return false;
      if (filter.billing && deriveBillingState(agent).state !== filter.billing) return false;
      if (filter.search) {
        const term = filter.search.toLowerCase();
        const matchesName = agent.name.toLowerCase().includes(term);
        const matchesHostname = agent.hostname.toLowerCase().includes(term);
        const matchesIp = agent.ipAddress?.toLowerCase().includes(term);
        if (!matchesName && !matchesHostname && !matchesIp) return false;
      }
      return true;
    });
  }, [agents, filter]);

  const loadFleetData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setRefreshing(!silent);

    try {
      const [agentsData, statsData, archivedData] = await Promise.all([
        api.getAgents(),
        api.getAgentStats(),
        api.getArchivedAgents(),
      ]);

      setAgents(Array.isArray(agentsData) ? agentsData : []);
      setFleetStats(statsData);
      setArchivedAgents(Array.isArray(archivedData) ? archivedData : []);
    } catch (error) {
      console.error('Error loading fleet data:', error);
      toast.error('Failed to load agent fleet data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadFleetData();

    // Set up real-time updates
    const interval = setInterval(() => {
      loadFleetData(true);
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [tenantId, currentPage, loadFleetData]);

  const handleStatusFilter = (status: string) => {
    setFilter(prev => ({
      ...prev,
      status: prev.status === status ? undefined : status
    }));
    setCurrentPage(1);
  };

  const handleSearch = (search: string) => {
    setFilter(prev => ({ ...prev, search: search || undefined }));
    setCurrentPage(1);
  };

  const handleAgentSelect = (agentId: string) => {
    const newSelected = new Set(selectedAgents);
    if (newSelected.has(agentId)) {
      newSelected.delete(agentId);
    } else {
      newSelected.add(agentId);
    }
    setSelectedAgents(newSelected);
  };

  const handleBulkAction = async (action: string) => {
    if (selectedAgents.size === 0) {
      toast.error('No agents selected');
      return;
    }

    try {
      if (action === 'archive') {
        for (const agentId of selectedAgents) {
          await api.archiveAgent(agentId);
        }
        toast.success(`Archived ${selectedAgents.size} agent(s)`);
      } else {
        for (const agentId of selectedAgents) {
          await api.synchronizeAgent(agentId);
        }
        toast.success(`${action} applied to ${selectedAgents.size} agents`);
      }

      setSelectedAgents(new Set());
      loadFleetData();
    } catch (error) {
      toast.error(`Failed to ${action} agents`);
    }
  };

  const handleArchiveAgent = async (agentId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (archiveConfirm !== agentId) {
      setArchiveConfirm(agentId);
      return;
    }
    try {
      await api.archiveAgent(agentId);
      toast.success('Agent archived -- audit history preserved');
      setArchiveConfirm(null);
      loadFleetData();
    } catch (error) {
      toast.error('Failed to archive agent');
    }
  };

  const handleRestoreAgent = async (agentId: string) => {
    try {
      await api.restoreAgent(agentId);
      toast.success('Agent restored to fleet');
      loadFleetData();
    } catch (error) {
      toast.error('Failed to restore agent');
    }
  };

  const formatLastSeen = (lastSeenAt: string) => {
    const date = new Date(lastSeenAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    status: keyof typeof statusColors;
    onClick?: () => void;
  }> = ({ title, value, status, onClick }) => {
    const Icon = statusIcons[status];

    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        className={`p-6 bg-white rounded-lg shadow cursor-pointer border-l-4 ${
          status === 'ONLINE' ? 'border-green-500' :
          status === 'ERROR' ? 'border-red-500' :
          status === 'MAINTENANCE' ? 'border-yellow-500' :
          'border-gray-500'
        }`}
        onClick={onClick}
      >
        <div className="flex items-center">
          <div className={`p-2 rounded-md ${statusColors[status]}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="ml-4">
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-semibold text-gray-900">{value}</p>
          </div>
        </div>
      </motion.div>
    );
  };

  const AgentRow: React.FC<{ agent: Agent }> = ({ agent }) => {
    const StatusIcon = statusIcons[agent.status];
    const isConfirming = archiveConfirm === agent.id;

    return (
      <motion.tr
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => navigate(`/agents/${agent.id}`)}
      >
        <td className="px-6 py-4 whitespace-nowrap">
          <input
            type="checkbox"
            checked={selectedAgents.has(agent.id)}
            onClick={(e) => e.stopPropagation()}
            onChange={() => handleAgentSelect(agent.id)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div className={`p-1 rounded-full ${statusColors[agent.status]}`}>
              <StatusIcon className="h-4 w-4" />
            </div>
            <div className="ml-3">
              <div className="text-sm font-medium text-gray-900">{agent.name}</div>
              <div className="text-sm text-gray-500">{agent.hostname}</div>
            </div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusColors[agent.status]}`}>
            {agent.status}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
          {agent.client.name}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {agent.ipAddress || 'N/A'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {agent.agentVersion}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {formatLastSeen(agent.lastSeenAt)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm">
          <BillingPill
            agent={agent}
            confirming={archiveConfirm === agent.id}
            onArchive={() => handleArchiveAgent(agent.id, { stopPropagation: () => undefined } as unknown as React.MouseEvent)}
          />
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm">
          {agent.debugErrorCount && agent.debugErrorCount > 0 ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full text-red-600 bg-red-100">
              <BugAntIcon className="h-3.5 w-3.5" />
              {agent.debugErrorCount}
            </span>
          ) : (
            <span className="text-gray-400">--</span>
          )}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex items-center justify-end space-x-2">
            <button
              onClick={(e) => { e.stopPropagation(); onAgentSelect?.(agent); }}
              className="text-blue-600 hover:text-blue-900"
            >
              View
            </button>
            <button
              onClick={(e) => handleArchiveAgent(agent.id, e)}
              onBlur={() => setArchiveConfirm(null)}
              className={`inline-flex items-center px-2 py-1 text-xs rounded ${
                isConfirming
                  ? 'text-red-700 bg-red-100 hover:bg-red-200'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
              title={isConfirming ? 'Click again to confirm' : 'Archive agent'}
            >
              <ArchiveBoxIcon className="h-4 w-4" />
              {isConfirming && <span className="ml-1">Confirm?</span>}
            </button>
          </div>
        </td>
      </motion.tr>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent Fleet</h1>
          <p className="text-gray-600">
            Manage and monitor your distributed security agents
          </p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => loadFleetData()}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
          {onAgentRegister && (
            <button
              onClick={onAgentRegister}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
            >
              <PlusIcon className="h-4 w-4" />
              <span>Register Agent</span>
            </button>
          )}
        </div>
      </div>

      {/* Fleet Statistics */}
      {fleetStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          <StatCard
            title="Total Agents"
            value={fleetStats.total}
            status="ONLINE"
            onClick={() => setFilter({})}
          />
          <StatCard
            title="Online"
            value={fleetStats.online}
            status="ONLINE"
            onClick={() => handleStatusFilter('ONLINE')}
          />
          <StatCard
            title="Offline"
            value={fleetStats.offline}
            status="OFFLINE"
            onClick={() => handleStatusFilter('OFFLINE')}
          />
          <StatCard
            title="Error"
            value={fleetStats.error}
            status="ERROR"
            onClick={() => handleStatusFilter('ERROR')}
          />
          <StatCard
            title="Maintenance"
            value={fleetStats.maintenance}
            status="MAINTENANCE"
            onClick={() => handleStatusFilter('MAINTENANCE')}
          />
        </div>
      )}

      {/* Billing state: silent hosts are still billed until day 30 (decision 68) */}
      <div className="bg-white p-4 rounded-lg shadow">
        <SilentHostsBanner
          agents={agents}
          active={filter.billing ?? null}
          onFilter={(state) => { setFilter(prev => ({ ...prev, billing: state ?? undefined })); setCurrentPage(1); }}
        />
      </div>

      {/* Filters and Search */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search agents..."
                value={filter.search || ''}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
            >
              <FunnelIcon className="h-4 w-4" />
              <span>Filters</span>
            </button>
          </div>

          {selectedAgents.size > 0 && (
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600">
                {selectedAgents.size} selected
              </span>
              <button
                onClick={() => handleBulkAction('maintenance')}
                className="px-3 py-1 text-xs font-medium text-yellow-800 bg-yellow-100 rounded-md hover:bg-yellow-200"
              >
                Maintenance
              </button>
              <button
                onClick={() => handleBulkAction('restart')}
                className="px-3 py-1 text-xs font-medium text-blue-800 bg-blue-100 rounded-md hover:bg-blue-200"
              >
                Restart
              </button>
              <button
                onClick={() => handleBulkAction('archive')}
                className="px-3 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Archive
              </button>
            </div>
          )}
        </div>

        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t pt-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <select
                    value={filter.status || ''}
                    onChange={(e) => setFilter(prev => ({
                      ...prev,
                      status: e.target.value || undefined
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Statuses</option>
                    <option value="ONLINE">Online</option>
                    <option value="OFFLINE">Offline</option>
                    <option value="ERROR">Error</option>
                    <option value="MAINTENANCE">Maintenance</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client
                  </label>
                  <select
                    value={filter.clientId || ''}
                    onChange={(e) => setFilter(prev => ({
                      ...prev,
                      clientId: e.target.value || undefined
                    }))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">All Clients</option>
                    {uniqueClients.map(([id, name]) => (
                      <option key={id} value={id}>{name}</option>
                    ))}
                  </select>
                </div>
                <div className="flex items-end">
                  <button
                    onClick={() => {
                      setFilter({});
                      setCurrentPage(1);
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Agents Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <input
                  type="checkbox"
                  checked={selectedAgents.size === filteredAgents.length && filteredAgents.length > 0}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedAgents(new Set(filteredAgents.map(a => a.id)));
                    } else {
                      setSelectedAgents(new Set());
                    }
                  }}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Agent
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Client
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                IP Address
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Version
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Seen
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Billing
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Debug Errors
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            <AnimatePresence>
              {filteredAgents.map((agent) => (
                <AgentRow key={agent.id} agent={agent} />
              ))}
            </AnimatePresence>
          </tbody>
        </table>

        {filteredAgents.length === 0 && (
          <div className="text-center py-12">
            <ComputerDesktopIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No agents found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {filter.search || filter.status || filter.clientId
                ? 'Try adjusting your filters'
                : 'Get started by registering your first agent'}
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Page {currentPage} of {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Archived Agents Section */}
      {archivedAgents.length > 0 && (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <button
            onClick={() => setShowArchived(!showArchived)}
            className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50"
          >
            <div className="flex items-center space-x-3">
              {showArchived
                ? <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                : <ChevronRightIcon className="h-5 w-5 text-gray-400" />
              }
              <ArchiveBoxIcon className="h-5 w-5 text-gray-400" />
              <span className="text-sm font-medium text-gray-700">
                Archived Agents
              </span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                {archivedAgents.length}
              </span>
            </div>
            <span className="text-xs text-gray-400">
              Audit history preserved
            </span>
          </button>

          <AnimatePresence>
            {showArchived && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="border-t border-gray-200"
              >
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Agent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Version
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Seen
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Archived
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {archivedAgents.map((agent) => (
                      <tr
                        key={agent.id}
                        className="hover:bg-gray-50 cursor-pointer text-gray-400"
                        onClick={() => navigate(`/agents/${agent.id}`)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <ArchiveBoxIcon className="h-4 w-4 text-gray-300" />
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-500">{agent.name}</div>
                              <div className="text-sm text-gray-400">{agent.hostname}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {agent.client.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {agent.agentVersion}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {formatLastSeen(agent.lastSeenAt)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                          {agent.archivedAt ? formatDate(agent.archivedAt) : '--'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/agents/${agent.id}`);
                              }}
                              className="text-blue-500 hover:text-blue-700"
                            >
                              View Audits
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRestoreAgent(agent.id);
                              }}
                              className="inline-flex items-center px-2 py-1 text-xs text-green-700 bg-green-50 rounded hover:bg-green-100"
                            >
                              <ArchiveBoxXMarkIcon className="h-4 w-4 mr-1" />
                              Restore
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
};

export default AgentFleetDashboard;
